/**
 * Phase 8 T-25 §29 — `/dev/smart-match-stages` QA sandbox.
 *
 * Lets a tester step through all four stages with canned data, without
 * hitting the real intake → canvas flow. Useful for:
 *   - Visual regression on the Clarification stage anatomy (§29.B).
 *   - Verifying the round cap (2) + `[Start looking →]` bypass.
 *   - Rendering the `<NextStepBridge>` 6-chip row with a fake ticked set.
 *
 * Not linked from the main nav. Accessed via `/dev/smart-match-stages`.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ClarificationStage,
  type ClarificationRound,
} from './components/ClarificationStage';
import { JdQuoteCard } from './components/JdQuoteCard';
import { ParaphraseTurn } from './components/ParaphraseTurn';
import { NextStepBridge } from './components/NextStepBridge';
import {
  buildFollowUpParaphrase,
  buildInitialParaphrase,
} from './lib/paraphraseStub';
import type {
  SmartMatchIntakeTab,
  SmartMatchStage,
} from './lib/smartMatchThreads';
import type { MatchResult } from './types';

const CANNED_PROMPT = `We need a Senior Product Designer with 5+ years of experience. Must be strong in Figma, have shipped B2B SaaS products end-to-end, and be comfortable working Remote-first with a distributed team across the US and EU.`;

/** Minimal canned match results — enough for the NextStepBridge preview. */
const CANNED_MATCHES: MatchResult[] = Array.from({ length: 6 }).map((_, i) => ({
  id: `m-${i}`,
  org_id: 'demo',
  candidate_id: `c-${i}`,
  job_id: 'j-0',
  composite_score: 0.95 - i * 0.04,
  dimension_scores: {
    skill_overlap: 0.9,
    semantic_similarity: 0.85,
    experience_alignment: 0.88,
    budget_fit: 0.9,
    location_fit: 0.8,
    deadline_feasibility: 0.85,
  },
  matched_skills: ['Figma', 'SaaS', 'B2B'],
  missing_skills: [],
  bonus_skills: [],
  recommendation: 'Strong match',
  match_version: 1,
  matched_at: new Date().toISOString(),
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  candidate: {
    id: `c-${i}`,
    org_id: 'demo',
    full_name: `Candidate ${i + 1}`,
    email: `candidate${i + 1}@example.com`,
    current_title: 'Senior Product Designer',
    current_company: 'Acme Co.',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  job: { id: 'j-0', title: 'Senior Product Designer' } as any,
}));

const STAGE_LABELS: Record<SmartMatchStage, string> = {
  intake: 'Stage 0 · Intake',
  clarify: 'Stage 1 · Clarify',
  stream: 'Stage 2 · Stream',
  results: 'Stage 3 · Results',
};

export function SmartMatchStagesSandboxPage() {
  const [stage, setStage] = useState<SmartMatchStage>('clarify');
  const [intakeTab, setIntakeTab] = useState<SmartMatchIntakeTab>('refine-search');
  const [rounds, setRounds] = useState<ClarificationRound[]>(() => [
    buildInitialParaphrase(CANNED_PROMPT),
  ]);
  const [roundCount, setRoundCount] = useState(0);
  const [thinking, setThinking] = useState(false);
  const tickedKeys = useMemo(() => new Set<string>(['c-0', 'c-1']), []);

  const pushRefinement = (text: string) => {
    const next = Math.min(roundCount + 1, 2);
    setRoundCount(next);
    setThinking(true);
    window.setTimeout(() => {
      setRounds((prev) => [
        ...prev,
        buildFollowUpParaphrase(CANNED_PROMPT, text, next),
      ]);
      setThinking(false);
    }, 500);
  };

  return (
    <div className="flex min-h-screen flex-col bg-[var(--vscode-bg-primary)]">
      {/* Dev header */}
      <div
        className="flex items-center gap-3 border-b px-6 py-3"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
      >
        <Link
          to="/smart-match"
          className="text-[12px] underline"
          style={{ color: 'var(--ggj-accent,#10B981)' }}
        >
          ← Smart Match
        </Link>
        <span className="text-[13px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
          Dev · Smart Match stages sandbox
        </span>
        <div
          role="tablist"
          aria-label="Stage picker"
          className="ml-auto inline-flex rounded-[10px] border p-0.5 text-[12px] font-medium"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            background: 'var(--vscode-bg-elevated)',
          }}
        >
          {(['intake', 'clarify', 'stream', 'results'] as const).map((s) => {
            const active = stage === s;
            return (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setStage(s)}
                className={`inline-flex h-7 items-center rounded-[8px] px-3 transition-colors ${
                  active
                    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                    : 'text-[color:var(--vscode-text-secondary)] hover:text-[color:var(--vscode-text-primary)]'
                }`}
              >
                {STAGE_LABELS[s]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stage body */}
      <div className="min-h-0 flex-1">
        {stage === 'intake' ? <StageIntakeStub /> : null}

        {stage === 'clarify' ? (
          <ClarificationStage
            threadId="sandbox-thread"
            prompt={CANNED_PROMPT}
            intakeTab={intakeTab}
            onIntakeTabChange={setIntakeTab}
            rounds={rounds}
            roundCount={roundCount}
            onSubmitRefinement={pushRefinement}
            isThinking={thinking}
            onStop={() => setThinking(false)}
            onStartLooking={(_scope) => setStage('stream')}
          />
        ) : null}

        {stage === 'stream' ? (
          <StageStreamStub onComplete={() => setStage('results')} />
        ) : null}

        {stage === 'results' ? (
          <div>
            <NextStepBridge
              threadId="sandbox-thread"
              visibleMatches={CANNED_MATCHES}
              tickedKeys={tickedKeys}
              collection={null}
              onShortlistTopN={() => undefined}
              onDraftOutreach={() => undefined}
              onCompareStub={() => undefined}
            />
            <div className="mx-auto max-w-[960px] px-6 py-6 space-y-3">
              <JdQuoteCard prompt={CANNED_PROMPT} />
              <ParaphraseTurn
                paraphrase={rounds[0]!.paraphrase}
                thoughtMs={rounds[0]!.thoughtMs}
              />
              <p className="text-[13px]" style={{ color: 'var(--vscode-text-secondary)' }}>
                {CANNED_MATCHES.length} candidate{CANNED_MATCHES.length === 1 ? '' : 's'} · scores
                92–76%.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StageIntakeStub() {
  return (
    <div className="mx-auto max-w-[640px] px-6 py-12">
      <p
        className="text-[22px] font-semibold"
        style={{ color: 'var(--vscode-text-primary)' }}
      >
        Hey QA, what can I help you find?
      </p>
      <p className="mt-2 text-[13px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
        (Canned stage — in production this is `/smart-match/new`.)
      </p>
    </div>
  );
}

function StageStreamStub({ onComplete }: { onComplete: () => void }) {
  return (
    <div className="mx-auto max-w-[640px] px-6 py-12 text-center">
      <p className="text-[13px]" style={{ color: 'var(--vscode-text-secondary)' }}>
        Evaluating candidates (3/6)…
      </p>
      <div className="mx-auto mt-4 flex w-64 justify-center">
        <div className="h-1 w-full overflow-hidden rounded bg-[var(--vscode-bg-elevated)]">
          <div className="h-full w-1/2 animate-pulse bg-emerald-500" />
        </div>
      </div>
      <button
        type="button"
        onClick={onComplete}
        className="mt-6 inline-flex h-8 items-center rounded-[10px] border px-3 text-[12px] font-medium"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          color: 'var(--vscode-text-secondary)',
        }}
      >
        (Jump to results)
      </button>
    </div>
  );
}
